import { useCallback, useEffect, useMemo, useState } from 'react'

import { Box, InfiniteScroll } from '@factorialco/deprecated-design-system'
import { F0ButtonProps, F0Card, IconType } from '@factorialco/f0-react'
import { SectionHeader } from '@factorialco/f0-react/dist/experimental'
import { Plus } from '@factorialco/f0-react/icons/app'
import { ComplaintsNotesQuery } from 'generated/resources/graphql'

import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { DeleteModal } from 'modules/complaints/components/Complaints/Common/DeleteModal'
import NoteCard from 'modules/complaints/components/Complaints/Complaint/Overview/InternalNotes/NoteCard'
import RitchTextEditorForNotes from 'modules/complaints/components/Complaints/Complaint/Overview/InternalNotes/RitchTextEditorForNotes'
import styles from 'modules/complaints/components/Complaints/Complaint/Overview/InternalNotes/index.module.css'
import {
  useComplaintsNotes,
  useCreateComplaintsNote,
  useDeleteComplaintsNote,
  useUpdateComplaintsNote,
} from 'modules/complaints/hooks/notes'

interface InternalNotesProps {
  complaintId: string
}

const VIEW_STATUS = {
  READ: 0,
  CREATE: 1,
  DELETE: 2,
  UPDATE: 3,
}
type ViewStatus = (typeof VIEW_STATUS)[keyof typeof VIEW_STATUS]

export default function InternalNotes({ complaintId }: InternalNotesProps) {
  const {
    notes,
    controls: { fetchMore, hasMore, loading: loadingComplaintsNotes },
  } = useComplaintsNotes({
    complaintIds: [complaintId],
    first: 10,
    sortOrder: { field: 'createdAt', order: 'desc' },
  })
  const { createComplaintsNote, loading: loadingCreateComplaintsNote } = useCreateComplaintsNote()
  const { deleteComplaintsNote, loading: loadingDeleteComplaintsNote } = useDeleteComplaintsNote()
  const { updateComplaintsNote, loading: loadingUpdateComplaintsNote } = useUpdateComplaintsNote()

  const [noteText, setNotesText] = useState<string>('')
  const [viewStatus, setViewStatus] = useState<ViewStatus>(VIEW_STATUS.READ)
  const [headerActionButton, setHeaderActionButton] = useState<
    | (Pick<F0ButtonProps, 'label' | 'onClick'> & {
        icon?: IconType
        variant?: 'default' | 'outline'
      })
    | undefined
  >(undefined)
  const [selectedNote, setSelectedNote] = useState<
    ComplaintsNotesQuery['complaints']['notesConnection']['edges'][number]['node'] | null
  >(null)

  const showEditorNote = useMemo(() => {
    return (
      viewStatus === VIEW_STATUS.CREATE ||
      (!loadingComplaintsNotes && !loadingCreateComplaintsNote && notes.length === 0)
    )
  }, [viewStatus, loadingComplaintsNotes, loadingCreateComplaintsNote, notes.length])

  useEffect(() => {
    setHeaderActionButton(
      !showEditorNote
        ? {
            icon: Plus,
            label: i18n.t('complaints.overview.internal_notes.add_note'),
            onClick: () => {
              setViewStatus(VIEW_STATUS.CREATE)
            },
          }
        : undefined
    )
  }, [loadingCreateComplaintsNote, showEditorNote])

  const saveNote = useCallback(async () => {
    if (!noteText?.trim()) return

    setViewStatus(VIEW_STATUS.READ)
    const result = await createComplaintsNote({
      text: noteText.trim(),
      complaintId: complaintId,
    })
    const handledResult = handleResult(result, () => {})

    if (handledResult.success) {
      setNotesText('')
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('complaints.overview.internal_notes.save_note_error_message'),
      })
      setViewStatus(VIEW_STATUS.CREATE)
    }
  }, [noteText, createComplaintsNote, complaintId])

  const deleteNote = useCallback(async () => {
    if (!selectedNote) return

    const result = await deleteComplaintsNote({
      id: selectedNote.id,
    })
    const handledResult = handleResult(result, () => {})

    if (handledResult.success) {
      setViewStatus(VIEW_STATUS.READ)
      setSelectedNote(null)
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('complaints.overview.internal_notes.delete_note_error_message'),
      })
      setViewStatus(VIEW_STATUS.DELETE)
    }
  }, [selectedNote, deleteComplaintsNote])

  const updateNote = useCallback(async () => {
    if (!selectedNote || !noteText) return

    const result = await updateComplaintsNote({
      id: selectedNote.id,
      text: noteText.trim(),
    })
    const handledResult = handleResult(result, () => {})

    if (handledResult.success) {
      setViewStatus(VIEW_STATUS.READ)
      setSelectedNote(null)
      setNotesText('')
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('complaints.overview.internal_notes.update_note_error_message'),
      })
      setViewStatus(VIEW_STATUS.UPDATE)
    }
  }, [selectedNote, noteText, updateComplaintsNote])

  return (
    <>
      <DeleteModal
        title={i18n.t('complaints.overview.internal_notes.delete_modal.title')}
        message={i18n.t('complaints.overview.internal_notes.delete_modal.message')}
        open={viewStatus === VIEW_STATUS.DELETE}
        onClose={() => setViewStatus(VIEW_STATUS.READ)}
        onConfirm={deleteNote}
        loading={loadingDeleteComplaintsNote}
      />
      <Box gap="s24" className={styles.notesContainer} height="full">
        <SectionHeader
          title={i18n.t('complaints.overview.internal_notes.title')}
          description={i18n.t('complaints.overview.internal_notes.description')}
          separator={undefined}
          action={headerActionButton}
        />

        {showEditorNote && (
          <RitchTextEditorForNotes
            onDiscard={() => setViewStatus(VIEW_STATUS.READ)}
            disableDiscard={!loadingComplaintsNotes && notes.length === 0}
            onChange={setNotesText}
            onSave={saveNote}
            noteText={noteText ?? ''}
          />
        )}

        {loadingCreateComplaintsNote && InternalNotesLoader}

        {loadingComplaintsNotes ? (
          InternalNotesLoader
        ) : (
          <InfiniteScroll
            loadMore={fetchMore}
            hasMore={hasMore}
            loader={<Box paddingTop="s24">{InternalNotesLoader}</Box>}
            initialLoad={false}
            threshold={10}
            ref={null}
          >
            <Box gap="s24">
              {notes.map((note) =>
                loadingUpdateComplaintsNote && selectedNote?.id === note.id ? (
                  InternalNotesLoader
                ) : (
                  <NoteCard
                    key={note.id + '_note_container'}
                    note={note}
                    showEditor={viewStatus === VIEW_STATUS.UPDATE && selectedNote?.id === note.id}
                    noteText={noteText}
                    onEdit={(text) => {
                      setSelectedNote(note)
                      setNotesText(text)
                      setViewStatus(VIEW_STATUS.UPDATE)
                    }}
                    onDelete={() => {
                      setSelectedNote(note)
                      setViewStatus(VIEW_STATUS.DELETE)
                    }}
                    onDiscardEdit={() => {
                      setViewStatus(VIEW_STATUS.READ)
                      setSelectedNote(null)
                      setNotesText('')
                    }}
                    onChange={setNotesText}
                    onSave={updateNote}
                  />
                )
              )}
            </Box>
          </InfiniteScroll>
        )}
      </Box>
    </>
  )
}

const InternalNotesLoader = <F0Card.Skeleton compact />
